import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import {
  uploadBackupRecordThunk,
  dismissBackupRecordThunk,
} from '../../../settings/settingsSlice';

interface BackupRecord {
  fileId: string;
  startedAt: number;
  teamId?: string;
  folderId?: string;
  uploadId?: string;
  status?: 'pending' | 'dismissed' | 'uploaded' | 'recovered'; // Optional for backwards compatibility
}

const getTimeAgo = (timestamp: number): string => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  return new Date(timestamp).toLocaleDateString();
};

export const useRecoveryLogic = () => {
  const dispatch = useDispatch();
  const { backupRecords, recoveryState } = useSelector(
    (state: RootState) => state.settings.recovery,
  );

  // Filter recoverable files based on status only (no time restriction)
  const recentRecoverableFiles = useMemo(() => {
    if (!backupRecords) {
      return [];
    }

    return backupRecords.filter((record) => {
      // Only show files that are actually pending (not uploaded, dismissed, or recovered)
      const isPending = record.status === 'pending';
      return isPending;
    });
  }, [backupRecords]);

  const handleRecover = (record: BackupRecord) => {
    dispatch(uploadBackupRecordThunk(record));
  };

  const handleDismiss = (fileId: string) => {
    dispatch(dismissBackupRecordThunk(fileId));
  };

  const firstRecord = recentRecoverableFiles[0];
  const isRecovering = firstRecord
    ? recoveryState[firstRecord.fileId]?.status === 'ongoing'
    : false;
  const progress = firstRecord
    ? recoveryState[firstRecord.fileId]?.progress || 0
    : 0;
  const hasMultiple = recentRecoverableFiles.length > 1;

  return {
    recentRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    hasRecoverableFiles: recentRecoverableFiles.length > 0,
  };
};

export type { BackupRecord };
